/**
 * Try Web Share API explicitly.
 * Returns true when shared, false otherwise.
 */
export async function tryWebShare({ title, text, url } = {}) {
  if (typeof navigator === 'undefined' || !navigator.share) return false;
  try {
    await navigator.share({ title, text, url });
    return true;
  } catch (e) {
    return false;
  }
}

function openExternal(url) {
  // On mobile, popup sizing is ignored anyway; use _blank + noopener for reliability
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
  if (isMobile) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    // Desktop: you can still try a sized window
    window.open(url, 'Share', 'width=520,height=520,left=200,top=200,noopener,noreferrer');
  }
}

/**
 * Open Gmail compose in new tab with prefilled fields.
 */
export function openGmailCompose({ to = '', subject = '', body = '' } = {}) {
  const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  openExternal(url);
}

/**
 * Open Outlook Web compose in new tab with prefilled fields.
 */
export function openOutlookCompose({ to = '', subject = '', body = '' } = {}) {
  const url = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  openExternal(url);
}

/**
 * Open WhatsApp share (web) in new tab with prefilled text.
 */
export function openWhatsAppShare({ text = '' } = {}) {
  // Use wa.me with text param; this works on mobile and web where WhatsApp is available
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  openExternal(url);
}

export function buildMailtoURI({ to, subject, body }) {
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  const paramString = params.length ? `?${params.join('&')}` : '';
  return `mailto:${encodeURIComponent(to || '')}${paramString}`;
}

export function openMailtoSafely(href) {
  try {
    // Use _blank to avoid navigating away from the SPA in most cases
    const w = window.open(href, '_blank', 'noopener,noreferrer');
    if (w) {
      w.focus?.();
      return;
    }
  } catch (e) {
    // ignore and fallback to iframe
  }

  // Fallback: use a hidden iframe to trigger the mail client without navigating
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = href;
    document.body.appendChild(iframe);
    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch (e) {
        /* ignore */
      }
    }, 1000);
  } catch (e) {
    // As a last resort, assign location (may navigate away)
    window.location.href = href;
  }
}

export function addUtmParams(url, params = {}) {
  try {
    const u = new URL(url);
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        u.searchParams.set(k, params[k]);
      }
    });
    return u.toString();
  } catch (e) {
    // Fallback for relative URLs or malformed input: append params manually
    const esc = (s) => encodeURIComponent(s);
    const pairs = Object.keys(params)
      .filter((k) => params[k] !== undefined && params[k] !== null)
      .map((k) => `${esc(k)}=${esc(params[k])}`);
    if (!pairs.length) return url;
    return url + (url.includes('?') ? '&' : '?') + pairs.join('&');
  }
}
